import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Menu, X, Phone, Mail, MapPin, Shield, Sparkles, CheckCircle2, Star, Upload, Image as ImageIcon, ArrowRight } from 'lucide-react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import WarrantyRegistration from './pages/WarrantyRegistration';
import WarrantyVerification from './pages/WarrantyVerification';
import AdminDashboard from './pages/AdminDashboard';
import DealerPortal from './pages/DealerPortal';
import Gallery from './pages/Gallery';
import MonumentPage from './pages/MonumentPage';
import { BlogList, BlogPost } from './pages/Blog';
import DealersPage from './pages/DealersPage';
import DealerRegisterPage from './pages/DealerRegisterPage';
import OrderDIYKitPage from './pages/OrderDIYKitPage';
import DIYKitLandingPage from './pages/DIYKitLandingPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


function GalleryCarousel({ projects }) {
  const [currentPage, setCurrentPage] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timerRef = useRef(null);

  // Filter to valid projects (must have both before and after images)
  const validProjects = projects.filter(p => {
    const imgs = p.images || [];
    return imgs.some(i => i.category === 'before') && imgs.some(i => i.category === 'after');
  });

  const itemsPerPage = typeof window !== 'undefined' && window.innerWidth < 768 ? 1 : window.innerWidth < 1024 ? 2 : 3;
  const totalPages = Math.ceil(validProjects.length / itemsPerPage);

  const goToPage = useCallback((page) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentPage(page);
      setIsTransitioning(false);
    }, 300);
  }, []);

  useEffect(() => {
    if (totalPages <= 1) return;
    timerRef.current = setInterval(() => {
      goToPage(prev => prev);
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentPage(prev => (prev + 1) % totalPages);
        setIsTransitioning(false);
      }, 300);
    }, 5000);
    return () => clearInterval(timerRef.current);
  }, [totalPages, goToPage]);

  const handleManualNav = (page) => {
    clearInterval(timerRef.current);
    goToPage(page);
    // Restart auto-rotation
    timerRef.current = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentPage(prev => (prev + 1) % totalPages);
        setIsTransitioning(false);
      }, 300);
    }, 5000);
  };

  const currentProjects = validProjects.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);

  if (validProjects.length === 0) return null;

  return (
    <div data-testid="gallery-carousel">
      <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
        {currentProjects.map((project) => {
          const beforeImg = (project.images || []).find(i => i.category === 'before');
          const afterImg = (project.images || []).find(i => i.category === 'after');
          return (
            <div key={project.id} className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300" data-testid={`gallery-item-${project.id}`}>
              <div className="grid grid-cols-2 gap-1">
                <div className="relative">
                  <img src={beforeImg.image_url} alt="Før" className="w-full h-64 object-cover" />
                  <div className="absolute bottom-2 left-2 bg-stone-900/80 text-white px-3 py-1 rounded text-sm font-medium">FØR</div>
                </div>
                <div className="relative">
                  <img src={afterImg.image_url} alt="Etter" className="w-full h-64 object-cover" />
                  <div className="absolute bottom-2 right-2 bg-emerald-800/80 text-white px-3 py-1 rounded text-sm font-medium">ETTER</div>
                </div>
              </div>
              {project.gravestone_name && (
                <div className="p-6">
                  <h3 className="text-xl font-medium text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>{project.gravestone_name}</h3>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-8" data-testid="gallery-carousel-dots">
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={i}
              onClick={() => handleManualNav(i)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${i === currentPage ? 'bg-stone-900 w-8' : 'bg-stone-300 hover:bg-stone-400'}`}
              aria-label={`Side ${i + 1}`}
              data-testid={`carousel-dot-${i}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Home() {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    images: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [galleryProjects, setGalleryProjects] = useState([]);
  const [latestPosts, setLatestPosts] = useState([]);

  useEffect(() => {
    fetch(`${API}/projects/public?project_type=gravstein`)
      .then(res => res.ok ? res.json() : [])
      .then(data => setGalleryProjects(data))
      .catch(() => {});
    fetch(`${API}/blog`)
      .then(res => res.ok ? res.json() : [])
      .then(data => setLatestPosts(data.slice(0, 3)))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitMessage('Takk! Vi har mottatt din forespørsel og vil kontakte deg snart.');
        setFormData({ name: '', email: '', phone: '', message: '', images: [] });
        setSelectedFiles([]);
      } else {
        const err = await response.json().catch(() => ({}));
        setSubmitMessage(err.detail || 'Kunne ikke sende skjemaet. Sjekk at alle felt er fylt ut og prøv igjen.');
      }
    } catch (error) {
      setSubmitMessage('Kunne ikke koble til serveren. Sjekk internettforbindelsen og prøv igjen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Limit to 3 images
    const filesToProcess = files.slice(0, 3);
    setSelectedFiles(filesToProcess);

    // Convert images to base64
    const base64Images = await Promise.all(
      filesToProcess.map(file => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = error => reject(error);
        });
      })
    );

    setFormData({
      ...formData,
      images: base64Images
    });
  };

  const removeImage = (index) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    const newImages = formData.images.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    setFormData({
      ...formData,
      images: newImages
    });
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(id);
      setIsMenuOpen(false);
    }
  };

  // Track active section on scroll
  React.useEffect(() => {
    const sections = ['home', 'services', 'dc-protect', 'diy-kit', 'gallery', 'about', 'contact'];
    const handleScroll = () => {
      const scrollPos = window.scrollY + 120;
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = document.getElementById(sections[i]);
        if (el && el.offsetTop <= scrollPos) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="scroll-smooth">
      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-md bg-white/90 border-b border-stone-100" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center mr-8">
              <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                Gravsteinoppfriskeren
              </h1>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-2">
              {[
                { id: 'home', label: 'Hjem' },
                { id: 'services', label: 'Tjenester' },
                { id: 'dc-protect', label: 'DC-Protect' },
                { id: 'diy-kit', label: 'Gjør-det-selv' },
                { id: 'gallery', label: 'Galleri' },
                { id: 'about', label: 'Om oss' },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`px-4 py-2 rounded-full font-medium text-sm transition-all duration-300 ${
                    activeSection === item.id
                      ? 'bg-emerald-800 text-white shadow-md'
                      : 'text-stone-700 hover:bg-stone-100 hover:text-stone-900'
                  }`}
                  data-testid={`nav-${item.id}`}
                >
                  {item.label}
                </button>
              ))}
              <button 
                onClick={() => navigate('/monument')} 
                className="px-4 py-2 rounded-full font-medium text-sm transition-all duration-300 text-stone-700 hover:bg-stone-100 hover:text-stone-900"
                data-testid="nav-monument"
              >
                Monumenter
              </button>
              <button 
                onClick={() => navigate('/blogg')} 
                className="px-4 py-2 rounded-full font-medium text-sm transition-all duration-300 text-stone-700 hover:bg-stone-100 hover:text-stone-900"
                data-testid="nav-blogg"
              >
                Blogg
              </button>
              <button 
                onClick={() => navigate('/forhandlere')} 
                className="px-4 py-2 rounded-full font-medium text-sm transition-all duration-300 text-stone-700 hover:bg-stone-100 hover:text-stone-900"
                data-testid="nav-forhandlere"
              >
                Forhandlere
              </button>
              <button 
                onClick={() => scrollToSection('contact')} 
                className={`rounded-full px-5 py-2 font-medium text-sm transition-all duration-300 ml-1 shadow-md hover:shadow-lg hover:-translate-y-0.5 ${
                  activeSection === 'contact'
                    ? 'bg-amber-600 text-white'
                    : 'bg-stone-900 text-white hover:bg-stone-800'
                }`}
                data-testid="nav-contact-btn"
              >
                Kontakt oss
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-stone-900"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden py-4 space-y-2" data-testid="mobile-menu">
              {[
                { id: 'home', label: 'Hjem' },
                { id: 'services', label: 'Tjenester' },
                { id: 'dc-protect', label: 'DC-Protect' },
                { id: 'diy-kit', label: 'Gjør-det-selv kit' },
                { id: 'gallery', label: 'Galleri' },
                { id: 'about', label: 'Om oss' },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`block w-full text-left rounded-lg px-4 py-3 font-medium transition-all duration-200 ${
                    activeSection === item.id
                      ? 'bg-emerald-800 text-white'
                      : 'text-stone-700 hover:bg-stone-100'
                  }`}
                >
                  {item.label}
                </button>
              ))}
              <button
                onClick={() => { navigate('/monument'); setIsMenuOpen(false); }}
                className="block w-full text-left rounded-lg px-4 py-3 font-medium transition-all duration-200 text-stone-700 hover:bg-stone-100"
              >
                Monumenter
              </button>
              <button
                onClick={() => { navigate('/blogg'); setIsMenuOpen(false); }}
                className="block w-full text-left rounded-lg px-4 py-3 font-medium transition-all duration-200 text-stone-700 hover:bg-stone-100"
              >
                Blogg
              </button>
              <button
                onClick={() => { navigate('/forhandlere'); setIsMenuOpen(false); }}
                className="block w-full text-left rounded-lg px-4 py-3 font-medium transition-all duration-200 text-stone-700 hover:bg-stone-100"
              >
                Forhandlere
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="block w-full text-center bg-stone-900 text-white rounded-full px-6 py-3 font-medium mt-2"
              >
                Kontakt oss
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center pt-20" data-testid="hero-section">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_mobile-laptop-web/artifacts/109i6lhq_Bakgrunnsbilde%20til%20facebook%20profil.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-stone-900/50 to-stone-900/20"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 
            className="text-5xl md:text-6xl font-bold tracking-tight leading-tight text-white mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="hero-title"
          >
            Bevar minnet,<br />
            gjenskap skjønnheten
          </h1>
          <p className="text-base md:text-lg leading-relaxed text-stone-100 max-w-2xl mx-auto mb-4">
            Vi fornyer gravsteiner over hele Norge med vår unike DC-Protect beskyttelse og garanterer at skriften holder seg vakker i 10 år.
          </p>
          <p className="text-lg md:text-xl leading-relaxed text-amber-300 font-semibold max-w-2xl mx-auto mb-8">
            Alt arbeid utføres på gravplassen - enklere og billigere for deg!
          </p>
          <button 
            onClick={() => scrollToSection('contact')}
            className="bg-stone-900 text-white hover:bg-stone-800 rounded-full px-8 py-6 text-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
            data-testid="hero-cta-btn"
          >
            Få tilbud i dag
          </button>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 md:py-32 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl md:text-5xl font-semibold tracking-tight text-stone-900 mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
              data-testid="services-title"
            >
              Våre tjenester
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-stone-600 max-w-3xl mx-auto">
              Vi tilbyr omfattende gravsteinoppfrisking med profesjonell rengjøring, lakking og vår unike beskyttelse.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Service 1 */}
            <div className="bg-white p-8 rounded-xl border border-stone-100 hover:border-stone-300 transition-all duration-300 hover:shadow-lg group" data-testid="service-onsite">
              <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mb-6 group-hover:bg-amber-100 transition-colors">
                <MapPin className="text-amber-700" size={32} />
              </div>
              <h3 className="text-2xl md:text-3xl font-medium text-stone-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Arbeid på gravplassen
              </h3>
              <p className="text-base leading-relaxed text-stone-600">
                Vi utfører alt arbeid direkte på gravplassen. Gravsteinen forblir på plass - enklere og billigere for deg!
              </p>
            </div>

            {/* Service 2 */}
            <div className="bg-white p-8 rounded-xl border border-stone-100 hover:border-stone-300 transition-all duration-300 hover:shadow-lg group" data-testid="service-cleaning">
              <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mb-6 group-hover:bg-emerald-100 transition-colors">
                <Sparkles className="text-emerald-800" size={32} />
              </div>
              <h3 className="text-2xl md:text-3xl font-medium text-stone-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Vask og rengjøring
              </h3>
              <p className="text-base leading-relaxed text-stone-600">
                Vi fjerner sopp, mose og urenheter fra gravsteinen med skånsomme, profesjonelle metoder som bevarer steinens integritet.
              </p>
            </div>

            {/* Service 3 */}
            <div className="bg-white p-8 rounded-xl border border-stone-100 hover:border-stone-300 transition-all duration-300 hover:shadow-lg group" data-testid="service-painting">
              <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mb-6 group-hover:bg-emerald-100 transition-colors">
                <Star className="text-emerald-800" size={32} />
              </div>
              <h3 className="text-2xl md:text-3xl font-medium text-stone-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Lakking av skrift
              </h3>
              <p className="text-base leading-relaxed text-stone-600">
                Vi legger to lag med høykvalitets lakk på slitt skrift og ikoner, som gjør dem tydelige og respektfulle igjen.
              </p>
            </div>

            {/* Service 4 */}
            <div className="bg-white p-8 rounded-xl border border-stone-100 hover:border-stone-300 transition-all duration-300 hover:shadow-lg group md:col-span-2 lg:col-span-3" data-testid="service-protection">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center group-hover:bg-amber-100 transition-colors flex-shrink-0">
                  <Shield className="text-amber-700" size={32} />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl md:text-3xl font-medium text-stone-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                    DC-Protect beskyttelse med 10 års garanti
                  </h3>
                  <p className="text-base leading-relaxed text-stone-600">
                    Vår unike beskyttelse av flytende silisiumdioksid (SiO2), også kjent som flytende kvarts, gir 10 års garanti på at skriften holder seg vakker. Vi er de eneste i Norge som tilbyr denne revolusjonerende beskyttelsen.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DC-Protect Section */}
      <section id="dc-protect" className="py-20 md:py-32 bg-stone-900 text-stone-50" data-testid="dc-protect-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded-full mb-6" data-testid="guarantee-badge">
                <Shield size={20} />
                <span className="font-bold">10 ÅRS GARANTI</span>
              </div>
              <h2 
                className="text-4xl md:text-5xl font-semibold tracking-tight mb-6"
                style={{ fontFamily: 'Playfair Display, serif' }}
                data-testid="dc-protect-title"
              >
                DC-Protect: Vår unike beskyttelse
              </h2>
              <p className="text-base md:text-lg leading-relaxed text-stone-300 mb-6">
                Etter over 10 år med utprøving og testing på ulike lakktyper og underlag, så har vi utviklet en revolusjonerende beskyttelse basert på flytende silisiumdioksid (SiO2), også kjent som flytende kvarts. Vi er de eneste i Norge som tilbyr denne beskyttelsen til våre kunder, og som gir 10 års garanti på at skriften skal holde seg like fin.
              </p>
              <p className="text-base md:text-lg leading-relaxed text-stone-300 mb-6">
                Fra og med 2024 har vi nå behandlet over 200 gravsteiner rundt om i Norge med vår unike beskyttelse.
              </p>

              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Forsegling av skrift med flytende kvarts
              </h3>
              <p className="text-base md:text-lg leading-relaxed text-stone-300 mb-4">
                Etter at skriften er oppfrisket, behandles hele fronten av gravsteinen med vårt beskyttelsesprodukt DC-Protect.
              </p>
              <p className="text-base md:text-lg leading-relaxed text-stone-300 mb-4">
                Produktet er basert på flytende kvarts (SiO₂) og danner etter herding et hardt og transparent beskyttelseslag på overflaten av steinen.
              </p>
              <p className="text-base md:text-lg leading-relaxed text-stone-300 mb-4">
                Behandlingen utføres <strong className="text-white">to ganger</strong>, slik at hele fronten av gravsteinen får en heldekkende beskyttelse.
              </p>
              <p className="text-base md:text-lg leading-relaxed text-stone-300 mb-3">
                Dette gjør at:
              </p>
              <ul className="space-y-2 mb-6 ml-1">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-emerald-400 flex-shrink-0 mt-1" size={20} />
                  <span className="text-stone-200">Lakken i skriften blir forseglet</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-emerald-400 flex-shrink-0 mt-1" size={20} />
                  <span className="text-stone-200">Vann og smuss får dårligere feste</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-emerald-400 flex-shrink-0 mt-1" size={20} />
                  <span className="text-stone-200">Skriften holder seg pen i mange år</span>
                </li>
              </ul>
              <p className="text-base md:text-lg leading-relaxed text-stone-300">
                Gravsteiner behandlet med DC-Protect får derfor <strong className="text-amber-400 text-lg">10 års garanti på skriften</strong>.
              </p>
            </div>
            <div>
              <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white text-center py-4 rounded-t-xl shadow-lg">
                <p className="text-2xl font-bold tracking-wide" style={{ fontFamily: 'Playfair Display, serif' }}>10 ÅRS GARANTI</p>
              </div>
              <img 
                src="https://customer-assets.emergentagent.com/job_6f167cb2-c653-4d56-91af-258001cc5e51/artifacts/48zqpn4v_Bilde%20til%20DC-Protect%20som%20viser%20belegg.png" 
                alt="DC-Protect kvartslag beskyttelse på gravstein"
                className="rounded-b-xl shadow-2xl w-full"
                data-testid="dc-protect-image"
              />
            </div>
          </div>
          
          {/* Warranty Certificate Section */}
          <div className="max-w-4xl mx-auto mt-16">
            <div className="bg-gradient-to-br from-stone-800 to-stone-900 rounded-2xl p-8 md:p-12 border border-amber-600/30">
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded-full mb-4">
                  <Shield size={20} />
                  <span className="font-bold">GARANTIBEVIS</span>
                </div>
                <h3 className="text-3xl md:text-4xl font-semibold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Få ditt eget garantibevis
                </h3>
                <p className="text-stone-300 text-lg leading-relaxed">
                  Etter endt oppfrisking med DC-Protect beskyttelse, mottar du et profesjonelt garantibevis på e-post med alle detaljer om din 10 års garanti.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_mobile-laptop-web/artifacts/m2djgp2f_04juni2025%20-%20Garantibevis%20gravstein%20-%20Gjeldokk.jpg"
                    alt="Eksempel på garantibevis"
                    className="rounded-xl shadow-2xl border-4 border-amber-600/50"
                    data-testid="warranty-certificate-image"
                  />
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="text-amber-400 flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h4 className="font-semibold text-white text-lg mb-1">Profesjonelt design</h4>
                      <p className="text-stone-300">Vakkert utformet garantibevis med bilde av din gravstein</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="text-amber-400 flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h4 className="font-semibold text-white text-lg mb-1">Alle viktige detaljer</h4>
                      <p className="text-stone-300">Garantinummer, dato for oppfrisking og garantiens sluttdato</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="text-amber-400 flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h4 className="font-semibold text-white text-lg mb-1">Trygghet i 10 år</h4>
                      <p className="text-stone-300">Dokumentasjon på at din gravstein er beskyttet</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="text-amber-400 flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h4 className="font-semibold text-white text-lg mb-1">Sendes på e-post</h4>
                      <p className="text-stone-300">Du får garantibeviset umiddelbart etter registrering</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DIY Kit Section */}
      <section id="diy-kit" className="py-20 md:py-32 bg-gradient-to-br from-emerald-50 to-stone-50" data-testid="diy-kit-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-emerald-800 text-white px-6 py-3 rounded-full mb-6" data-testid="coming-soon-badge">
              <Sparkles size={20} />
              <span className="font-bold">KOMMER SNART</span>
            </div>
            <h2 
              className="text-4xl md:text-5xl font-semibold tracking-tight text-stone-900 mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
              data-testid="diy-kit-title"
            >
              Beskytt gravstein som ikke trenger oppfrisking, og få 10 års garanti
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-stone-600 max-w-3xl mx-auto">
              Har du en gravstein med god skrift som ikke trenger oppfrisking? Med vårt gjør-det-selv kit kan du enkelt påføre DC-Protect beskyttelsen selv og få 10 års garanti.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12">
            <div className="bg-white p-8 md:p-12 rounded-2xl shadow-xl border border-stone-200">
              <h3 className="text-3xl font-semibold text-stone-900 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Perfekt for deg som ønsker å:
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="text-emerald-800 flex-shrink-0 mt-1" size={28} />
                  <div>
                    <h4 className="font-semibold text-stone-900 text-lg mb-1">Utsette kostbar oppfrisking</h4>
                    <p className="text-stone-600">Beskytt gravsteinen før skriften blir slitt og trenger dyr lakking.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="text-emerald-800 flex-shrink-0 mt-1" size={28} />
                  <div>
                    <h4 className="font-semibold text-stone-900 text-lg mb-1">Gjøre det selv</h4>
                    <p className="text-stone-600">Enkelt å påføre - ingen profesjonell hjelp nødvendig.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="text-emerald-800 flex-shrink-0 mt-1" size={28} />
                  <div>
                    <h4 className="font-semibold text-stone-900 text-lg mb-1">Få samme garanti</h4>
                    <p className="text-stone-600">10 års garanti på beskyttelsen - akkurat som våre profesjonelle tjenester.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="text-emerald-800 flex-shrink-0 mt-1" size={28} />
                  <div>
                    <h4 className="font-semibold text-stone-900 text-lg mb-1">Spare penger</h4>
                    <p className="text-stone-600">Rimeligere enn full oppfriskingstjeneste.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-emerald-900 to-emerald-700 p-8 md:p-12 rounded-2xl text-white shadow-2xl">
                <div className="flex items-center gap-3 mb-6">
                  <Shield size={48} className="text-amber-300" />
                  <div>
                    <p className="text-6xl font-bold">10 år</p>
                    <p className="text-xl font-medium text-emerald-100">Garanti</p>
                  </div>
                </div>
                <h3 className="text-2xl md:text-3xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Hva får du i kittet?
                </h3>
                <ul className="space-y-3 text-emerald-50">
                  <li className="flex items-center gap-3">
                    <CheckCircle2 size={24} className="flex-shrink-0 text-amber-300" />
                    <span>DC-Protect beskyttelsesprodukt</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle2 size={24} className="flex-shrink-0 text-amber-300" />
                    <span>Påføringsverktøy og instruksjoner</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle2 size={24} className="flex-shrink-0 text-amber-300" />
                    <span>Rengjøringsmateriell</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle2 size={24} className="flex-shrink-0 text-amber-300" />
                    <span>Garantisertifikat</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle2 size={24} className="flex-shrink-0 text-amber-300" />
                    <span>Trinn-for-trinn veiledning</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-8 text-center">
            <h3 className="text-2xl font-semibold text-stone-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Bestill ditt gjør-det-selv kit
            </h3>
            <p className="text-stone-700 mb-2">
              Kittet er klart for bestilling! Pris <strong>1 500 kr inkl. mva</strong> + 99 kr i frakt.
            </p>
            <p className="text-stone-600 text-sm mb-6">
              Hvert kit inneholder DC-Protect, forvask, mikrofiberkluter, hansker og garantinummer med QR-kode.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/bestill-kit')}
                className="bg-emerald-800 text-white hover:bg-emerald-900 rounded-full px-8 py-4 text-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
                data-testid="diy-kit-order-btn"
              >
                Bestill kit nå
              </button>
              <button
                onClick={() => navigate('/diy-kit')}
                className="bg-white text-emerald-800 hover:bg-stone-50 border-2 border-emerald-800 rounded-full px-8 py-4 text-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
                data-testid="diy-kit-learn-more-btn"
              >
                Les mer om kittet
              </button>
              <button
                onClick={() => navigate('/registrer-garanti')}
                className="bg-stone-900 text-white hover:bg-stone-800 rounded-full px-8 py-4 text-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1 flex items-center justify-center gap-2"
                data-testid="diy-kit-register-warranty-btn"
              >
                <Shield size={20} />
                Registrer garanti
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-20 md:py-32 bg-stone-50" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl md:text-5xl font-semibold tracking-tight text-stone-900 mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
              data-testid="gallery-title"
            >
              Før og etter
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-stone-600">
              Se hvordan vi transformerer gravsteiner fra slitne til vakre igjen.
            </p>
          </div>

          {galleryProjects.length > 0 ? (
            <GalleryCarousel projects={galleryProjects} />
          ) : (
            <div className="text-center py-12 bg-white rounded-2xl border border-stone-200">
              <p className="text-stone-500 text-lg">Galleriet oppdateres snart med ekte bilder fra våre prosjekter!</p>
              <p className="text-stone-400 mt-2">Kontakt oss for å se eksempler på vårt arbeid.</p>
            </div>
          )}

          <div className="text-center mt-12">
            <button
              onClick={() => navigate('/galleri')}
              className="inline-block bg-stone-900 text-white hover:bg-stone-800 rounded-full px-8 py-3 font-medium transition-all duration-300 hover:-translate-y-0.5 shadow-md hover:shadow-lg"
              data-testid="gallery-see-all-btn"
            >
              Se hele galleriet
            </button>
          </div>
        </div>
      </section>

      {/* Latest Blog Posts Section */}
      {latestPosts.length > 0 && (
        <section className="py-20 md:py-32 bg-white" data-testid="latest-blog-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-12">
              <div>
                <span className="text-sm font-semibold text-emerald-800 tracking-wider uppercase mb-2 block">Nytt fra oss</span>
                <h2 
                  className="text-4xl md:text-5xl font-semibold tracking-tight text-stone-900"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                  data-testid="latest-blog-title"
                >
                  Siste fra bloggen
                </h2>
              </div>
              <button
                onClick={() => navigate('/blogg')}
                className="hidden sm:inline-flex items-center gap-2 text-stone-600 hover:text-stone-900 font-medium transition-colors"
                data-testid="blog-see-all-link"
              >
                Se alle innlegg <ArrowRight size={16} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {latestPosts.map((post) => (
                <article
                  key={post.id}
                  onClick={() => navigate(`/blogg/${post.slug}`)}
                  className="bg-stone-50 rounded-2xl overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group relative"
                  data-testid={`latest-post-${post.id}`}
                >
                  {post.published_date && (Date.now() - new Date(post.published_date).getTime() < 14 * 24 * 60 * 60 * 1000) && (
                    <div className="absolute top-3 right-3 z-10 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md animate-pulse" data-testid={`new-badge-${post.id}`}>
                      NY!
                    </div>
                  )}
                  {post.cover_image ? (
                    <div className="h-48 overflow-hidden">
                      <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  ) : (
                    <div className="h-48 bg-gradient-to-br from-emerald-800 to-stone-900 flex items-center justify-center">
                      <span className="text-white/30 text-6xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>B</span>
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      {post.category && <span className="text-xs font-semibold text-emerald-800 bg-emerald-50 px-2.5 py-1 rounded-full">{post.category}</span>}
                      {post.published_date && <span className="text-xs text-stone-400">{new Date(post.published_date).toLocaleDateString('no-NO', { day: 'numeric', month: 'long', year: 'numeric' })}</span>}
                    </div>
                    <h3 className="text-lg font-semibold text-stone-900 mb-2 group-hover:text-emerald-800 transition-colors line-clamp-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {post.title}
                    </h3>
                    {post.excerpt && <p className="text-sm text-stone-500 line-clamp-2">{(() => { let t = post.excerpt.replace(/<[^>]*>/g, ''); t = t.replace(/img\s+src=["'][^"']*["'][^.]*/gi, ''); t = t.replace(/\s*(alt|style|class|src|href)=["'][^"']*["']\s*/gi, ''); return t.replace(/\s+/g, ' ').trim(); })()}</p>}
                  </div>
                </article>
              ))}
            </div>
            <div className="text-center mt-10 sm:hidden">
              <button
                onClick={() => navigate('/blogg')}
                className="inline-block bg-stone-900 text-white hover:bg-stone-800 rounded-full px-8 py-3 font-medium transition-colors"
              >
                Se alle innlegg
              </button>
            </div>
          </div>
        </section>
      )}


      {/* About Section */}
      <section id="about" className="py-20 md:py-32 bg-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 
              className="text-4xl md:text-5xl font-semibold tracking-tight text-stone-900 mb-8 text-center"
              style={{ fontFamily: 'Playfair Display, serif' }}
              data-testid="about-title"
            >
              Om oss
            </h2>
            <div className="space-y-6 text-base md:text-lg leading-relaxed text-stone-600">
              <p>
                Vi driver med oppfrisking av gravsteiner rundt om i Norge. Med godkjenning fra over tre fjerdedeler av de kirkelige fellesråd i Norge, og stadig nye godkjenninger, er vi en pålitelig partner for gravsteinvedlikehold.
              </p>
              <div className="bg-amber-50 p-6 rounded-xl border-l-4 border-amber-600">
                <h3 className="text-xl font-semibold text-stone-900 mb-3">Hvorfor vi er annerledes</h3>
                <p className="text-stone-700">
                  <strong>Vi utfører alt arbeid på gravplassen!</strong> Dette betyr at gravsteinen forblir på plass under hele prosessen. For deg som kunde betyr dette mindre stress, lavere kostnader, og ingen bekymring for transport eller skade under flytting.
                </p>
              </div>
              <p>
                Vår prosess inkluderer grundig vask og rengjøring av gravsteiner for sopp og mose, påføring av 2 lag med lakk der skriften er slitt, og til slutt legger vi vår unike DC-Protect beskyttelse over fronten av gravsteinen.
              </p>
              <p>
                Vi er de eneste som tilbyr denne beskyttelsen til våre kunder. De som velger å beskytte fronten av gravsteinen – dvs. skrift og lakkerte ikoner – får 10 års garanti på beskyttelsen.
              </p>
              
              <div className="bg-stone-50 p-8 rounded-xl border border-stone-200 mt-12">
                <h3 className="text-2xl md:text-3xl font-medium text-stone-900 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Våre fremtidsplaner
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="text-emerald-800 flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h4 className="font-semibold text-stone-900 mb-1">Forhandlernett</h4>
                      <p className="text-stone-600">
                        Vi bygger et nettverk av personer og firma som allerede utfører gravsteinoppfriskinger. De vil få mulighet til å kjøpe et beskyttelseskit for å tilby samme tjeneste.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="text-emerald-800 flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h4 className="font-semibold text-stone-900 mb-1">Gjør-det-selv kit</h4>
                      <p className="text-stone-600">
                        For de som har ansvar for en gravstein uten slitt skrift, tilbyr vi et DIY-kit hvor de selv kan påføre beskyttelsen og få 10 års garanti.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 md:py-32 bg-stone-50" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 
                className="text-4xl md:text-5xl font-semibold tracking-tight text-stone-900 mb-4"
                style={{ fontFamily: 'Playfair Display, serif' }}
                data-testid="contact-title"
              >
                Kontakt oss
              </h2>
              <p className="text-base md:text-lg leading-relaxed text-stone-600">
                Få et uforpliktende tilbud eller still oss spørsmål om våre tjenester.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div className="bg-white p-8 rounded-xl border border-stone-200 shadow-lg">
                <form onSubmit={handleSubmit} data-testid="contact-form">
                  <div className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-stone-700 mb-2">
                        Navn *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-stone-700 mb-2">
                        E-post *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent"
                        data-testid="contact-email-input"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-stone-700 mb-2">
                        Telefon *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent"
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-stone-700 mb-2">
                        Melding *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows="4"
                        value={formData.message}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent"
                        data-testid="contact-message-input"
                      ></textarea>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">
                        Bilder av gravsteinen (valgfritt, maks 3 bilder)
                      </label>
                      <div className="border-2 border-dashed border-stone-300 rounded-lg p-6 text-center hover:border-emerald-800 transition-colors">
                        <input
                          type="file"
                          id="images"
                          accept="image/*"
                          multiple
                          onChange={handleFileChange}
                          className="hidden"
                          data-testid="contact-image-input"
                        />
                        <label
                          htmlFor="images"
                          className="cursor-pointer flex flex-col items-center gap-2"
                        >
                          <Upload className="text-stone-400" size={32} />
                          <p className="text-sm text-stone-600">
                            Klikk for å laste opp bilder
                          </p>
                          <p className="text-xs text-stone-500">
                            PNG, JPG eller JPEG (maks 3 bilder)
                          </p>
                        </label>
                      </div>
                      {selectedFiles.length > 0 && (
                        <div className="mt-4 space-y-2" data-testid="uploaded-images-preview">
                          {selectedFiles.map((file, index) => (
                            <div key={index} className="flex items-center justify-between bg-stone-50 p-3 rounded-lg">
                              <div className="flex items-center gap-3">
                                <ImageIcon className="text-emerald-800" size={20} />
                                <span className="text-sm text-stone-700">{file.name}</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeImage(index)}
                                className="text-red-600 hover:text-red-800 text-sm"
                                data-testid={`remove-image-${index}`}
                              >
                                Fjern
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-stone-900 text-white hover:bg-stone-800 rounded-full px-8 py-4 text-lg font-medium transition-all duration-300 disabled:opacity-50"
                      data-testid="contact-submit-btn"
                    >
                      {isSubmitting ? 'Sender...' : 'Send forespørsel'}
                    </button>
                    {submitMessage && (
                      <p className={`text-center ${submitMessage.includes('Takk') ? 'text-emerald-800' : 'text-red-600'}`} data-testid="contact-submit-message">
                        {submitMessage}
                      </p>
                    )}
                  </div>
                </form>
              </div>

              {/* Contact Info */}
              <div className="space-y-8">
                <div className="bg-white p-8 rounded-xl border border-stone-200 shadow-lg">
                  <h3 className="text-2xl font-medium text-stone-900 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Kontaktinformasjon
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-4" data-testid="contact-info-phone">
                      <Phone className="text-emerald-800 flex-shrink-0 mt-1" size={24} />
                      <div>
                        <p className="font-medium text-stone-900">Telefon</p>
                        <p className="text-stone-600">48 14 77 77</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4" data-testid="contact-info-email">
                      <Mail className="text-emerald-800 flex-shrink-0 mt-1" size={24} />
                      <div>
                        <p className="font-medium text-stone-900">E-post</p>
                        <p className="text-stone-600">robert@DC-protect.no</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4" data-testid="contact-info-location">
                      <MapPin className="text-emerald-800 flex-shrink-0 mt-1" size={24} />
                      <div>
                        <p className="font-medium text-stone-900">Kontaktperson</p>
                        <p className="text-stone-600">Robert</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-emerald-50 p-8 rounded-xl border border-emerald-200">
                  <div className="flex items-center gap-3 mb-4">
                    <Shield className="text-emerald-800" size={32} />
                    <h3 className="text-xl font-semibold text-stone-900">
                      Godkjent av kirkelige fellesråd
                    </h3>
                  </div>
                  <p className="text-stone-700">
                    Vi har godkjenning fra over 3/4 av Norges kirkelige fellesråd og får stadig nye godkjenninger.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-900 text-stone-400 py-20" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Gravsteinoppfriskeren
              </h3>
              <p className="text-stone-400 mb-4">
                Profesjonell gravsteinoppfrisking med 10 års garanti på DC-Protect beskyttelse.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Hurtiglenker</h4>
              <div className="space-y-2">
                <button onClick={() => scrollToSection('services')} className="block text-stone-400 hover:text-white">
                  Tjenester
                </button>
                <button onClick={() => scrollToSection('dc-protect')} className="block text-stone-400 hover:text-white">
                  DC-Protect
                </button>
                <button onClick={() => scrollToSection('diy-kit')} className="block text-stone-400 hover:text-white">
                  Gjør-det-selv kit
                </button>
                <button onClick={() => navigate('/galleri')} className="block text-stone-400 hover:text-white">
                  Galleri
                </button>
                <button onClick={() => navigate('/monument')} className="block text-stone-400 hover:text-white">
                  Monumenter
                </button>
                <button onClick={() => scrollToSection('about')} className="block text-stone-400 hover:text-white">
                  Om oss
                </button>
                <button onClick={() => navigate('/registrer-garanti')} className="block text-stone-400 hover:text-white">
                  Registrer garanti
                </button>
                <button onClick={() => navigate('/blogg')} className="block text-stone-400 hover:text-white">
                  Blogg
                </button>
                <button onClick={() => navigate('/forhandlere')} className="block text-stone-400 hover:text-white">
                  Finn forhandler
                </button>
              </div>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Kontakt</h4>
              <div className="space-y-2">
                <p>Robert</p>
                <p>Telefon: 48 14 77 77</p>
                <p>E-post: robert@DC-protect.no</p>
                <p>www.gravsteinoppfriskeren.no</p>
              </div>
            </div>
          </div>
          <div className="border-t border-stone-800 mt-12 pt-8 text-center">
            <p>&copy; 2025 Gravsteinoppfriskeren. Alle rettigheter reservert.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/registrer-garanti" element={<WarrantyRegistration />} />
      <Route path="/garanti/:warrantyNumber" element={<WarrantyVerification />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/forhandler" element={<DealerPortal />} />
      <Route path="/galleri" element={<Gallery />} />
      <Route path="/monument" element={<MonumentPage />} />
      <Route path="/blogg" element={<BlogList />} />
      <Route path="/blogg/:slug" element={<BlogPost />} />
      <Route path="/forhandlere" element={<DealersPage />} />
      <Route path="/forhandler/registrer/:token" element={<DealerRegisterPage />} />
      <Route path="/bestill-kit" element={<OrderDIYKitPage />} />
      <Route path="/diy-kit" element={<DIYKitLandingPage />} />
    </Routes>
  );
}

export default App;
